import React, { useState } from 'react';
import { 
  Card, Typography, Button, Table, Space, Tag, Input, Modal, Form, Row, Col, Select, InputNumber, DatePicker, Switch, Drawer, Badge, Spin
} from 'antd';
import MDEditor from '@uiw/react-md-editor';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SearchOutlined, UserOutlined, CalendarOutlined, DownloadOutlined, HeartOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useToast } from '../../../contexts/ToastContext';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import apiService from '../../../services/api';
import { JobPostingResponse, JobApplicationResponse } from '../../../types';
import { ApiDefinition } from '../../../config';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const JobPostingsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingJobPosting, setEditingJobPosting] = useState<JobPostingResponse | null>(null);
  const [form] = Form.useForm();
  const [applicationsDrawerVisible, setApplicationsDrawerVisible] = useState(false);
  const [selectedJobPosting, setSelectedJobPosting] = useState<JobPostingResponse | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [description, setDescription] = useState<string>('');
  const [requirements, setRequirements] = useState<string>('');
  const [interviewModalVisible, setInterviewModalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<JobApplicationResponse | null>(null);
  const [interviewForm] = Form.useForm();
  const [candidateDetailModalVisible, setCandidateDetailModalVisible] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [candidateDetailLoading, setCandidateDetailLoading] = useState(false);
  const [interviewPage, setInterviewPage] = useState(1);
  const interviewPageSize = 20;

  const employmentTypes = [
    { value: 'Full-time', label: 'Tam Zamanlı' },
    { value: 'Part-time', label: 'Yarı Zamanlı' },
    { value: 'Contract', label: 'Sözleşmeli' },
    { value: 'Internship', label: 'Staj' },
    { value: 'Freelance', label: 'Serbest' }
  ];


  const statusOptions = [
    { value: 'Pending', label: 'Beklemede', color: 'orange' },
    { value: 'Reviewed', label: 'İncelendi', color: 'blue' },
    { value: 'Accepted', label: 'Kabul Edildi', color: 'green' },
    { value: 'Rejected', label: 'Reddedildi', color: 'red' }
  ];

  const jobPostingsParams = { page: pagination.current, pageSize: pagination.pageSize, searchText };
  const { data: jobPostingsResult, isLoading: loading } = useQuery({
    queryKey: companyProfileQueryKeys.jobPostings(jobPostingsParams),
    queryFn: async () => {
      const r = await apiService.getJobPostings({ page: pagination.current, pageSize: pagination.pageSize, searchText });
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 2,
  });
  const jobPostings = jobPostingsResult?.data ?? [];
  const totalCount = jobPostingsResult?.totalCount ?? 0;

  const { data: categories = [] } = useQuery({
    queryKey: companyProfileQueryKeys.categories,
    queryFn: async () => {
      const r = await apiService.getCategories();
      if (!r.success) throw new Error(r.message);
      return (r.data ?? []).map((c: { id: number; name: string }) => ({ id: c.id, name: c.name }));
    },
    staleTime: 1000 * 60 * 10,
  });

  const { data: applicationsData, isLoading: applicationsLoading } = useQuery({
    queryKey: selectedJobPosting ? companyProfileQueryKeys.jobApplications(selectedJobPosting.id) : ['skip'],
    queryFn: async () => {
      const r = await apiService.getJobApplications(selectedJobPosting!.id);
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    enabled: !!selectedJobPosting?.id,
    staleTime: 1000 * 60,
  });
  const applications = applicationsData?.data ?? [];

  const { data: interviewsResult, isLoading: interviewsLoading } = useQuery({
    queryKey: [companyProfileQueryKeys.interviews, interviewPage],
    queryFn: async () => {
      const r = await apiService.getCompanyInterviews(interviewPage, interviewPageSize);
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 2,
  });
  const interviews = interviewsResult?.data ?? [];
  const interviewTotal = interviewsResult?.totalCount ?? 0;

  const { data: favorites = [] } = useQuery({
    queryKey: companyProfileQueryKeys.favorites({}),
    queryFn: async () => {
      const r = await apiService.getCandidateFavorites();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: pool = [], refetch: refetchPool } = useQuery({
    queryKey: companyProfileQueryKeys.pool({}),
    queryFn: async () => {
      const r = await apiService.getCandidatePool();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const createJobMutation = useMutation({
    mutationFn: (data: any) => apiService.createJobPosting(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('İş ilanı başarıyla oluşturuldu');
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'job-postings'] });
        setModalVisible(false);
      } else toast.error(response.message || 'İş ilanı oluşturulamadı');
    },
    onError: (error: unknown) => {
      const apiMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      toast.error(apiMessage || 'İş ilanı oluşturulurken hata oluştu');
    },
  });

  const updateJobMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiService.updateJobPosting(id, data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('İş ilanı başarıyla güncellendi');
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'job-postings'] });
        setModalVisible(false);
      } else toast.error(response.message || 'İş ilanı güncellenemedi');
    },
    onError: (error: unknown) => {
      const apiMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      toast.error(apiMessage || 'İş ilanı güncellenirken hata oluştu');
    },
  });

  const deleteJobMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteJobPosting(id),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('İş ilanı başarıyla silindi');
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'job-postings'] });
      } else toast.error(response.message || 'İş ilanı silinemedi');
    },
    onError: (error: unknown) => {
      const apiMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      toast.error(apiMessage || 'İş ilanı silinirken hata oluştu');
    },
  });

  const updateApplicationMutation = useMutation({
    mutationFn: ({ applicationId, status }: { applicationId: number; status: string }) =>
      apiService.updateJobApplication(applicationId, { status }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Başvuru durumu güncellendi');
        if (selectedJobPosting) queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.jobApplications(selectedJobPosting.id) });
      } else toast.error(response.message || 'Başvuru durumu güncellenemedi');
    },
    onError: () => toast.error('Başvuru durumu güncellenirken hata oluştu'),
  });

  const createInterviewMutation = useMutation({
    mutationFn: (data: any) => apiService.createInterview(data),
    onSuccess: (response) => {
      if (response.success) {
        setInterviewModalVisible(false);
        interviewForm.resetFields();
        setSelectedApplication(null);
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.interviews });
        if (selectedJobPosting) queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.jobApplications(selectedJobPosting.id) });
        toast.success('Mülakat başarıyla planlandı');
      } else toast.error(response.message || 'Mülakat planlanırken hata oluştu');
    },
    onError: () => toast.error('Mülakat planlanırken hata oluştu'),
  });

  const addToFavoritesMutation = useMutation({
    mutationFn: (data: { menteeId: number | null | undefined; mentorId: number | null | undefined; notes: string }) =>
      apiService.addToFavorites({ ...data, menteeId: data.menteeId ?? null, mentorId: data.mentorId ?? null }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Aday favorilere eklendi');
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.favorites({}) });
      } else toast.error(response.message || 'Favorilere eklenirken hata oluştu');
    },
    onError: () => toast.error('Favorilere eklenirken hata oluştu'),
  });

  const addToPoolMutation = useMutation({
    mutationFn: (data: { menteeId: number | null | undefined; mentorId: number | null | undefined; notes: string; status: string }) =>
      apiService.addToPool({ ...data, menteeId: data.menteeId ?? null, mentorId: data.mentorId ?? null }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Aday havuza eklendi');
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.pool({}) });
      } else toast.error(response.message || 'Havuza eklenirken hata oluştu');
    },
    onError: () => toast.error('Havuza eklenirken hata oluştu'),
  });

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingJobPosting(null);
    setDescription('');
    setRequirements('');
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (jobPosting: JobPostingResponse) => {
    setEditingJobPosting(jobPosting);
    setDescription(jobPosting.description || '');
    setRequirements(jobPosting.requirements || '');
    form.setFieldsValue({
      ...jobPosting,
      applicationDeadline: dayjs(jobPosting.applicationDeadline),
      categoryId: jobPosting.categoryId
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const jobPostingData = {
        ...values,
        description,
        requirements,
        isActive: values.isActive ?? false,
        applicationDeadline: values.applicationDeadline.format('YYYY-MM-DD')
      };
      if (editingJobPosting) {
        updateJobMutation.mutate({ id: editingJobPosting.id, data: jobPostingData });
      } else {
        createJobMutation.mutate(jobPostingData);
      }
    } catch (error) {
      // form validation
    }
  };

  const handleDelete = (id: number) => {
    deleteJobMutation.mutate(id);
  };

  const handleViewApplications = (jobPosting: JobPostingResponse) => {
    setSelectedJobPosting(jobPosting);
    setApplicationsDrawerVisible(true);
  };

  const handleApplicationStatusUpdate = (applicationId: number, status: string) => {
    updateApplicationMutation.mutate({ applicationId, status });
  };

  const handleScheduleInterview = (application: JobApplicationResponse) => {
    setSelectedApplication(application);
    setInterviewModalVisible(true);
    interviewForm.resetFields();
  };

  const handleInterviewSubmit = async () => {
    try {
      const values = await interviewForm.validateFields();
      if (!selectedApplication) return;
      const interviewData = {
        jobApplicationId: selectedApplication.id,
        interviewDate: values.interviewDate.toISOString(),
        interviewType: values.interviewType,
        notes: values.notes,
        interviewerName: values.interviewerName,
        location: values.location
      };
      createInterviewMutation.mutate(interviewData);
    } catch (error) {
      toast.error('Mülakat planlanırken hata oluştu');
    }
  };

  const hasInterview = (applicationId: number) => {
    return interviews.some(interview => interview.jobApplicationId === applicationId);
  };

  const isInFavorites = (application: JobApplicationResponse) => {
    return favorites.some((fav: any) => 
      (application.applicantType === 'Mentee' && fav.menteeId === application.menteeId) ||
      (application.applicantType === 'Mentor' && fav.mentorId === application.mentorId)
    );
  };

  const isInPool = (application: JobApplicationResponse) => {
    return pool.some((p: any) => 
      (application.applicantType === 'Mentee' && p.menteeId === application.menteeId) ||
      (application.applicantType === 'Mentor' && p.mentorId === application.mentorId)
    );
  };

  const handleViewCandidateDetail = async (application: JobApplicationResponse) => {
    setSelectedCandidate(application);
    setCandidateDetailLoading(true);
    setCandidateDetailModalVisible(true);
    
    try {
      if (application.applicantType === 'Mentee' && application.menteeId) {
        // Temel bilgileri yükle
        const response = await apiService.getMenteeDetail(application.menteeId);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MenteeDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.title = response.data.title || null;
          detail.hourlyRate = null; // Mentee'de yok
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMenteeSkillsById(application.menteeId),
              apiService.getMenteeEducationsById(application.menteeId),
              apiService.getMenteeWorkExperiencesById(application.menteeId),
              apiService.getMenteeProjectsById(application.menteeId),
              apiService.getMenteeCertificatesById(application.menteeId)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentee detail sections:', err);
          }
          
          setSelectedCandidate({ ...application, detail });
        } else {
          toast.error('Mentee detayları yüklenemedi');
        }
      } else if (application.applicantType === 'Mentor' && application.mentorId) {
        // Temel bilgileri yükle
        const response = await apiService.getMentorDetail(application.mentorId);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MentorDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.hourlyRate = response.data.hourlyRate;
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMentorSkills(application.mentorId),
              apiService.getMentorEducations(application.mentorId),
              apiService.getMentorWorkExperiences(application.mentorId),
              apiService.getMentorProjects(application.mentorId),
              apiService.getMentorCertificates(application.mentorId)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentor detail sections:', err);
          }
          
          setSelectedCandidate({ ...application, detail });
        } else {
          toast.error('Mentor detayları yüklenemedi');
        }
      } else {
        toast.error('Aday ID\'si bulunamadı');
      }
    } catch (error) {
      console.error('Error loading candidate detail:', error);
      toast.error('Aday detayları yüklenirken hata oluştu');
    } finally {
      setCandidateDetailLoading(false);
    }
  };

  const handleDownloadResume = async (application: JobApplicationResponse) => {
    try {
      if (!application.resumeFileName) {
        toast.warning('CV bulunamadı');
        return;
      }

      const response = await fetch(`${ApiDefinition.apiUrl}/api/jobapplication/download-resume/${application.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = application.resumeFileName || 'resume.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('CV indirildi');
      } else {
        toast.error('CV indirilemedi');
      }
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error('CV indirilirken hata oluştu');
    }
  };

  const handleAddToFavorites = (application: JobApplicationResponse) => {
    addToFavoritesMutation.mutate({
      menteeId: application.applicantType === 'Mentee' ? application.menteeId : null,
      mentorId: application.applicantType === 'Mentor' ? application.mentorId : null,
      notes: `İş ilanı: ${application.jobTitle} - Başvuru tarihi: ${dayjs(application.appliedAt).format('DD.MM.YYYY')}`
    });
  };

  const handleAddToPool = (application: JobApplicationResponse) => {
    addToPoolMutation.mutate({
      menteeId: application.applicantType === 'Mentee' ? application.menteeId : null,
      mentorId: application.applicantType === 'Mentor' ? application.mentorId : null,
      notes: `İş ilanı: ${application.jobTitle} - Başvuru tarihi: ${dayjs(application.appliedAt).format('DD.MM.YYYY')}`,
      status: 'Available'
    });
  };

  const columns = [
    {
      title: 'İlan Başlığı',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: JobPostingResponse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
        </div>
      ),
    },
    {
      title: 'Kategori',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (categoryName: string) => categoryName || '-',
    },
    {
      title: 'Tip',
      dataIndex: 'employmentType',
      key: 'employmentType',
      render: (type: string) => type || '-',
    },
    {
      title: 'Deneyim',
      key: 'experience',
      render: (_: unknown, record: JobPostingResponse) => {
        const min = record.minExperienceYears;
        const max = record.maxExperienceYears;
        if (min != null && max != null) return `${min}-${max} yıl`;
        return record.experienceLevel || '-';
      },
    },
    {
      title: 'Maaş',
      key: 'salary',
      render: (_: any, record: JobPostingResponse) => {
        if (record.salaryMin && record.salaryMax) {
          return `${record.salaryMin.toLocaleString()}₺ - ${record.salaryMax.toLocaleString()}₺`;
        } else if (record.salaryMin) {
          return `${record.salaryMin.toLocaleString()}₺+`;
        }
        return '-';
      },
    },
    {
      title: 'Son Başvuru',
      dataIndex: 'applicationDeadline',
      key: 'applicationDeadline',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Başvurular',
      dataIndex: 'applicationCount',
      key: 'applicationCount',
      render: (count: number, record: JobPostingResponse) => (
        <Badge count={count} size="small">
          <Button 
            type="text" 
            icon={<UserOutlined />}
            onClick={() => handleViewApplications(record)}
            style={{ color: 'var(--primary-500)' }}
          >
            Başvurular
          </Button>
        </Badge>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: JobPostingResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  const applicationColumns = [
    {
      title: 'Aday',
      key: 'applicant',
      render: (_: any, record: JobApplicationResponse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.applicantName}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.applicantEmail}</div>
          <Tag color={record.applicantType === 'Mentee' ? 'blue' : 'green'}>
            {record.applicantType}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Başvuru Tarihi',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = statusOptions.find(s => s.value === status);
        return <Tag color={statusConfig?.color || 'default'}>{statusConfig?.label || status}</Tag>;
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: JobApplicationResponse) => (
        <Space wrap>
          <Select
            value={record.status}
            onChange={(value) => handleApplicationStatusUpdate(record.id, value)}
            style={{ width: 120 }}
            size="small"
          >
            {statusOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          
          {hasInterview(record.id) ? (
            <Button
              size="small"
              icon={<CalendarOutlined />}
              disabled
            >
              Mülakat Planlandı
            </Button>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<CalendarOutlined />}
              onClick={() => handleScheduleInterview(record)}
            >
              Mülakat Planla
            </Button>
          )}
          
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewCandidateDetail(record)}
          >
            Detay
          </Button>
          
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadResume(record)}
          >
            CV İndir
          </Button>
          
          <Button
            size="small"
            icon={<HeartOutlined />}
            onClick={() => handleAddToFavorites(record)}
            disabled={isInFavorites(record)}
            style={{ color: isInFavorites(record) ? 'var(--success)' : undefined }}
          >
            {isInFavorites(record) ? 'Favoride' : 'Favori'}
          </Button>
          
          <Button
            size="small"
            icon={<TeamOutlined />}
            onClick={() => handleAddToPool(record)}
            disabled={isInPool(record)}
            style={{ color: isInPool(record) ? 'var(--success)' : undefined }}
          >
            {isInPool(record) ? 'Havuzda' : 'Havuza Ekle'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="panel-tab-content">
      <TabListLoading loading={loading}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3} style={{ margin: 0, color: '#1f2937' }}>
              İş İlanları
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              İş ilanlarınızı yönetin ve başvuruları inceleyin
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni İlan Ekle
            </Button>
          </Col>
        </Row>

        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="İlan başlığı, departman veya lokasyon ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ borderRadius: '6px' }}
            />
          </Col>
          <Col span={4}>
            <Button 
              type="primary" 
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{ width: '100%' }}
            >
              Ara
            </Button>
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={jobPostings}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 10 }));
            },
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} ilan`,
          }}
        />
      </Card>
      </TabListLoading>

      {/* Job Posting Modal */}
      <Modal
        title={editingJobPosting ? 'İş İlanı Düzenle' : 'Yeni İş İlanı'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="title"
            label="İlan Başlığı"
            rules={[{ required: true, message: 'İlan başlığı gerekli' }]}
          >
            <Input placeholder="Örn: Frontend Developer" />
          </Form.Item>

          <Form.Item label="İş Tanımı">
            <MDEditor
              value={description}
              onChange={(val) => setDescription(val || '')}
              data-color-mode="light"
              height={200}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="location"
                label="Lokasyon"
              >
                <Input placeholder="Örn: İstanbul" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="employmentType"
                label="Çalışma Tipi"
              >
                <Select placeholder="Seçiniz">
                  {employmentTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="minExperienceYears"
                label="Min. Deneyim (yıl)"
              >
                <InputNumber style={{ width: '100%' }} min={0} max={99} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="maxExperienceYears"
                label="Maks. Deneyim (yıl)"
              >
                <InputNumber style={{ width: '100%' }} min={0} max={99} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="salaryMin"
                label="Minimum Maaş (₺)"
              >
                <InputNumber style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="salaryMax"
                label="Maksimum Maaş (₺)"
              >
                <InputNumber style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="applicationDeadline"
                label="Son Başvuru Tarihi"
                rules={[{ required: true, message: 'Son başvuru tarihi gerekli' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Gereksinimler">
            <MDEditor
              value={requirements}
              onChange={(val) => setRequirements(val || '')}
              data-color-mode="light"
              height={200}
            />
          </Form.Item>

          <Form.Item
            name="categoryId"
            label="Kategori"
          >
            <Select placeholder="Kategori seçin" allowClear>
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="benefits"
            label="Yan Haklar"
          >
            <TextArea rows={3} placeholder="Sunulan yan haklar..." />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Aktif"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Interview Planning Modal */}
      <Modal
        title="Mülakat Planla"
        open={interviewModalVisible}
        onOk={handleInterviewSubmit}
        onCancel={() => setInterviewModalVisible(false)}
        width={600}
        okText="Planla"
        cancelText="İptal"
      >
        <Form
          form={interviewForm}
          layout="vertical"
          initialValues={{
            interviewType: 'Online',
            interviewerName: '',
            location: '',
            notes: ''
          }}
        >
          <Form.Item
            name="interviewDate"
            label="Mülakat Tarihi ve Saati"
            rules={[{ required: true, message: 'Mülakat tarihi seçiniz' }]}
          >
            <DatePicker
              showTime
              format="DD.MM.YYYY HH:mm"
              style={{ width: '100%' }}
              placeholder="Mülakat tarihi seçiniz"
            />
          </Form.Item>

          <Form.Item
            name="interviewType"
            label="Mülakat Türü"
            rules={[{ required: true, message: 'Mülakat türü seçiniz' }]}
          >
            <Select placeholder="Mülakat türü seçiniz">
              <Option value="Online">Online (Video Call)</Option>
              <Option value="In-Person">Yüz Yüze</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="interviewerName"
            label="Mülakat Yapan Kişi"
            rules={[{ required: true, message: 'Mülakat yapan kişiyi giriniz' }]}
          >
            <Input placeholder="Mülakat yapan kişinin adı" />
          </Form.Item>

          <Form.Item
            name="location"
            label="Konum / Meeting Link"
          >
            <Input placeholder="Fiziksel adres veya video meeting linki" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <TextArea rows={3} placeholder="Mülakat hakkında notlar..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Applications Drawer */}
      <Drawer
        title={`${selectedJobPosting?.title} - Başvurular`}
        placement="right"
        width={800}
        open={applicationsDrawerVisible}
        onClose={() => setApplicationsDrawerVisible(false)}
      >
        <div className="panel-tab-content">
          <Table
            scroll={{ x: 'max-content' }}
            columns={applicationColumns}
            dataSource={applications}
            rowKey="id"
            loading={applicationsLoading}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </Drawer>

      {/* Candidate Detail Modal */}
      <Modal
        title={`${selectedCandidate?.applicantName} - Aday Detayları`}
        open={candidateDetailModalVisible}
        onCancel={() => {
          setCandidateDetailModalVisible(false);
          setSelectedCandidate(null);
        }}
        width={1000}
        footer={[
          <Button key="close" onClick={() => {
            setCandidateDetailModalVisible(false);
            setSelectedCandidate(null);
          }}>
            Kapat
          </Button>
        ]}
      >
        {candidateDetailLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Aday detayları yükleniyor...</div>
          </div>
        ) : selectedCandidate?.detail ? (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Kişisel Bilgiler" size="small">
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Text strong>Ad Soyad:</Text> {selectedCandidate.detail.firstName} {selectedCandidate.detail.lastName}
                    </Col>
                    <Col span={12}>
                      <Text strong>E-posta:</Text> {selectedCandidate.detail.email}
                    </Col>
                    <Col span={12}>
                      <Text strong>Telefon:</Text> {selectedCandidate.detail.phoneNumber || 'Belirtilmemiş'}
                    </Col>
                    <Col span={12}>
                      <Text strong>Şehir:</Text> {selectedCandidate.detail.city || 'Belirtilmemiş'}
                    </Col>
                    {selectedCandidate.applicantType === 'Mentor' && selectedCandidate.detail.title && (
                      <Col span={12}>
                        <Text strong>Ünvan:</Text> {selectedCandidate.detail.title}
                      </Col>
                    )}
                    {selectedCandidate.applicantType === 'Mentor' && selectedCandidate.detail.hourlyRate && (
                      <Col span={12}>
                        <Text strong>Saatlik Ücret:</Text> ₺{selectedCandidate.detail.hourlyRate}
                      </Col>
                    )}
                    <Col span={24}>
                      <Text strong>Bio:</Text> {selectedCandidate.detail.bio || 'Belirtilmemiş'}
                    </Col>
                  </Row>
                </Card>
              </Col>
              
              {selectedCandidate.detail.skills && selectedCandidate.detail.skills.length > 0 && (
                <Col span={24}>
                  <Card title="Yetenekler" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.skills.map((skill: any, index: number) => (
                        <Tag key={index} color="blue">{skill.skillName}</Tag>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.educations && selectedCandidate.detail.educations.length > 0 && (
                <Col span={24}>
                  <Card title="Eğitim Bilgileri" size="small">
                    {selectedCandidate.detail.educations.map((edu: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{edu.schoolName}</Text> - {edu.degree || edu.department}
                        <br />
                        <Text type="secondary">{(edu.department || edu.fieldOfStudy || '')} • {dayjs(edu.startDate).format('YYYY')} - {edu.endDate ? dayjs(edu.endDate).format('YYYY') : (edu.isGraduated ? '-' : 'Günümüz')}</Text>
                        {edu.description && <div style={{ marginTop: '4px' }}>{edu.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.workExperiences && selectedCandidate.detail.workExperiences.length > 0 && (
                <Col span={24}>
                  <Card title="İş Deneyimi" size="small">
                    {selectedCandidate.detail.workExperiences.map((work: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{work.companyName}</Text> - {work.position}
                        <br />
                        <Text type="secondary">{dayjs(work.startDate).format('MMM YYYY')} - {(work.isCurrentJob || !work.endDate) ? 'Günümüz' : dayjs(work.endDate).format('MMM YYYY')}</Text>
                        {work.description && <div style={{ marginTop: '4px' }}>{work.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.projects && selectedCandidate.detail.projects.length > 0 && (
                <Col span={24}>
                  <Card title="Projeler" size="small">
                    {selectedCandidate.detail.projects.map((project: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{project.projectName}</Text>
                        <br />
                        <Text type="secondary">{dayjs(project.startDate).format('MMM YYYY')} - {project.endDate ? dayjs(project.endDate).format('MMM YYYY') : 'Devam ediyor'}</Text>
                        {project.projectDescription && <div style={{ marginTop: '4px' }}>{project.projectDescription}</div>}
                        {project.technologies && (
                          <div style={{ marginTop: '4px' }}>
                            <Space wrap>
                              {(Array.isArray(project.technologies) ? project.technologies : project.technologies.split(',')).map((tech: string, techIndex: number) => (
                                <Tag key={techIndex} color="green">{tech.trim()}</Tag>
                              ))}
                            </Space>
                          </div>
                        )}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}
              
              {selectedCandidate.detail.certificates && selectedCandidate.detail.certificates.length > 0 && (
                <Col span={24}>
                  <Card title="Sertifikalar" size="small">
                    {selectedCandidate.detail.certificates.map((cert: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{cert.certificateName}</Text>
                        <br />
                        <Text type="secondary">{cert.issuingOrganization} • {dayjs(cert.issueDate).format('MMM YYYY')}</Text>
                        {cert.description && <div style={{ marginTop: '4px' }}>{cert.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {selectedCandidate.applicantType === 'Mentor' && selectedCandidate.detail.favouriteMentees && selectedCandidate.detail.favouriteMentees.length > 0 && (
                <Col span={24}>
                  <Card title="Referans verdiği menteeler" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.favouriteMentees.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button key={m.id} type="link" size="small" onClick={() => window.open(`/mentee/${m.id}`, '_blank')}>
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              {selectedCandidate.applicantType === 'Mentee' && selectedCandidate.detail.referencedByMentors && selectedCandidate.detail.referencedByMentors.length > 0 && (
                <Col span={24}>
                  <Card title="Referansları" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.referencedByMentors.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button key={m.id} type="link" size="small" onClick={() => window.open(`/mentor/${m.id}`, '_blank')}>
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text type="secondary">Aday detayları yüklenemedi</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobPostingsTab;